// src/app/provider/config.service.ts
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataService } from '../services/data.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private APP_KEY = 'appSettings';
  private USER_KEY = 'userPreferences';
  private CONFIG_KEY = 'config';

  private _defaultSettings = {
    appName: environment.APP_NAME,
    appLanguage: 'en',
    appDirection: 'ltr',
    appNotification: false,
    appDarkMode: false,
    currentUser: null,
    testEnabled: null,
    setTheme: 'light3',
    loginData: {
      phone: 0,
      password: null,
    },
  };

  private _defaultUserPreferences = {
    lastActivity: null,
    favoriteColor: 'blue',
    savedSearches: [],
  };
    private _defaultConfigurations = {
    lastActivity: null,
    favoriteColor: 'blue',
    savedSearches: [],
  };

  private _appSettings = new BehaviorSubject<any>(this._defaultSettings);
  public appSettings$: Observable<any> = this._appSettings.asObservable();

  private _userPreferences = new BehaviorSubject<any>(this._defaultUserPreferences);
  public userPreferences$: Observable<any> = this._userPreferences.asObservable();

    private _configurations = new BehaviorSubject<any>(this._defaultConfigurations);
  public configurations$: Observable<any> = this._configurations.asObservable();
  currentSettings: any;

  constructor(
    private dataService: DataService
  ) {
    this.loadInitialSettings();
    this.loadInitialConfigurations();
    this.loadInitialUserPreferences();
    this.intialDataLoad();
    
  }

  // --- Core Settings Logic (appSettings) ---

  private async intialDataLoad(){
        this.dataService.getData('configurations').subscribe(async (response) => {
      if (response) {
        console.log("yes subscribe!!!!")
       await this.updateConfigurations(response.organization).then(() => console.log('User preferences updated.'));
       console.log(this.configurationsSync(),"this is appPayload new");
       console.log(this.appSettingsSync,"this is settings new");
       console.log(this.userPreferencesSync,"this is user new");

      }
    });
  }



  private async loadInitialSettings(): Promise<void> {
    const { value } = await Preferences.get({ key: this.APP_KEY });

    if (value) {
      try {
        const parsedSettings = JSON.parse(value);
        this._appSettings.next({ ...this._defaultSettings, ...parsedSettings });
      } catch (e) {
        this._appSettings.next(this._defaultSettings);
        await this.saveInternal(this.APP_KEY, this._defaultSettings);
      }
    } else {
      this._appSettings.next(this._defaultSettings);
      await this.saveInternal(this.APP_KEY, this._defaultSettings);
    }
  }

  public get appSettingsSync(): any {
    this.currentSettings = this._appSettings.getValue();
    return this._appSettings.getValue();
  }

  public async updateAppSettings(newPartialSettings: any): Promise<void> {
    const updatedSettings = { ...this.appSettingsSync, ...newPartialSettings };
    this._appSettings.next(updatedSettings);
    await this.saveInternal(this.APP_KEY, updatedSettings);
  }

  public async clearAppSettings(): Promise<void> {
    this._appSettings.next(this._defaultSettings);
    await this.saveInternal(this.APP_KEY, this._defaultSettings);
  }

  // --- User Preferences Logic (userPreferences) ---

  private async loadInitialUserPreferences(): Promise<void> {
    const { value } = await Preferences.get({ key: this.USER_KEY });

    if (value) {
      try {
        const parsedPrefs = JSON.parse(value);
        this._userPreferences.next({ ...this._defaultUserPreferences, ...parsedPrefs });
      } catch (e) {
        this._userPreferences.next(this._defaultUserPreferences);
        await this.saveInternal(this.USER_KEY, this._defaultUserPreferences);
      }
    } else {
      this._userPreferences.next(this._defaultUserPreferences);
      await this.saveInternal(this.USER_KEY, this._defaultUserPreferences);
    }
  }

  public get userPreferencesSync(): any {
    return this._userPreferences.getValue();
  }

  public async updateUserPreferences(newPartialPreferences: any): Promise<void> {
    const updatedPreferences = { ...this.userPreferencesSync, ...newPartialPreferences };
    this._userPreferences.next(updatedPreferences);
    await this.saveInternal(this.USER_KEY, updatedPreferences);
  }

  public async clearUserPreferences(): Promise<void> {
    this._userPreferences.next(this._defaultUserPreferences);
    await this.saveInternal(this.USER_KEY, this._defaultUserPreferences);
  }

    // --- User Preferences Logic (userPreferences) ---

  private async loadInitialConfigurations(): Promise<void> {
    const { value } = await Preferences.get({ key: this.CONFIG_KEY });

    if (value) {
      try {
        const parsedPrefs = JSON.parse(value);
        this._configurations.next({ ...this._defaultConfigurations, ...parsedPrefs });
      } catch (e) {
        this._configurations.next(this._defaultConfigurations);
        await this.saveInternal(this.CONFIG_KEY, this._defaultConfigurations);
      }
    } else {
      this._configurations.next(this._defaultConfigurations);
      await this.saveInternal(this.CONFIG_KEY, this._defaultConfigurations);
    }
   
  }

  public configurationsSync(): any {
    return this._configurations.getValue();
  }

  public async updateConfigurations(newPartialPreferences: any): Promise<void> {
    const updatedPreferences = { ...this.configurationsSync, ...newPartialPreferences };
    this._configurations.next(updatedPreferences);
    await this.saveInternal(this.CONFIG_KEY, updatedPreferences);
  }

  public async clearConfigurations(): Promise<void> {
    this._configurations.next(this._defaultConfigurations);
    await this.saveInternal(this.CONFIG_KEY, this._defaultConfigurations);
  }

  // --- Internal Persistence Helper ---

  private async saveInternal(key: string, dataToSave: any): Promise<void> {
    await Preferences.set({
      key: key,
      value: JSON.stringify(dataToSave),
    });
  }

  // --- General Storage Utilities (Kept for other non-preference data) ---

  public async setStorage(keyStorage: string, dataToStore: any): Promise<void> {
    await Preferences.set({
      key: keyStorage,
      value: JSON.stringify(dataToStore),
    });
  }

  public async getStorage(keyStorage: string): Promise<any> {
    const { value } = await Preferences.get({ key: keyStorage });
    return value ? JSON.parse(value) : null;
  }

  public async removeStore(keyStorage: string): Promise<void> {
    await Preferences.remove({ key: keyStorage });
  }
}

/**
 * Basic Example Use of All Created Methods
 *
 * import { ConfigService } from './config.service';
 *
 * constructor(private configService: ConfigService) {
 * // 1. Synchronous access (Guaranteed to return current data or defaults immediately)
 * const currentMode = this.configService.appSettingsSync.appDarkMode;
 * console.log('Current Dark Mode (Sync):', currentMode);
 *
 * // 2. Asynchronous updates (Persists to storage and updates all subscribers)
 * this.configService.updateAppSettings({ appDarkMode: true, appLanguage: 'es' })
 * .then(() => console.log('App settings updated.'));
 *
 * // 3. Subscription to changes (Reactive updates for components)
 * this.configService.appSettings$.subscribe(settings => {
 * console.log('App Settings Updated (Reactive):', settings);
 * });
 *
 * // 4. User Preferences methods
 * this.configService.updateUserPreferences({ favoriteColor: 'green' })
 * .then(() => console.log('User preferences updated.'));
 *
 * // 5. Clear methods (Resets to defaults and saves the defaults)
 * this.configService.clearAppSettings()
 * .then(() => console.log('App settings cleared and reset to defaults.'));
 *
 * // 6. General Storage (For data outside of main app/user preferences structure)
 * const customData = { sessionId: 'xyz123', timestamp: Date.now() };
 * this.configService.setStorage('sessionData', customData).then(() => {
 * this.configService.getStorage('sessionData').then(data => {
 * console.log('General Storage Data:', data);
 * });
 * });
 * }
 */